import React from 'react'
import styled from 'styled-components'
import { UserPermission } from '../models/client/user'
import { text } from '../styles/mixins/text'
import { Check } from './Check'
import { Spacer } from './Spacer'

const PERMISSION_SCOPES = ['admin'] as const
type PermissionScope = (typeof PERMISSION_SCOPES)[number]
type Content<T extends UserPermission> = {
    key: T
    label: string
    description: string
}

type Scope<T extends UserPermission> = {
    key: PermissionScope
    description: string
    content: Content<T>[]
}

type PermissionScopes = Scope<'audits' | 'users' | 'checkins'>

const PERMISSION_CONTENT: Array<PermissionScopes> = [
    {
        key: 'admin',
        description: 'perform all platform actions',
        content: [
            {
                key: 'users',
                label: 'users',
                description: 'manage users in your company',
            },
            {
                key: 'audits',
                label: 'audits',
                description: 'review data changes',
            },
            {
                key: 'checkins',
                label: 'checkins',
                description: 'review driver checkin board',
            },
        ],
    },
]

type Props = {
    selections: Array<UserPermission>
    onChange: (nextPermissions: UserPermission[]) => void
}

type CheckedItemProps = {
    label: string
    description: string
    disabled?: boolean
    checked?: boolean
    isScope?: boolean
    onClick: () => void
}

const PermissionItemElement = (props: CheckedItemProps): JSX.Element => {
    const { label, description, disabled, checked, isScope, onClick } = props
    return (
        <GridRow>
            <Row disabled={disabled}>
                {!isScope ? <Spacer size={4} /> : <div />}
                <Check
                    isChecked={Boolean(checked)}
                    onChange={() => {
                        onClick?.()
                    }}
                    disabled={disabled}
                />
                <Spacer size={2} />
                <PermissionLabel isScope={isScope}>{label}</PermissionLabel>
            </Row>
            <Row>
                <DescriptionLabel>{description}</DescriptionLabel>
            </Row>
        </GridRow>
    )
}

export const PermissionsSettings = (props: Props): JSX.Element => {
    const { selections, onChange } = props
    const permissions = new Set([...selections])
    const adminSelected = permissions.has('audits') && permissions.has('users')
    const handleSelect = (selection: PermissionScope | UserPermission): void => {
        const nextPermissions = new Set([...selections])

        switch (selection) {
            case 'admin':
                if (adminSelected) {
                    nextPermissions.delete('audits')
                    nextPermissions.delete('users')
                    break
                }
                nextPermissions.add('audits')
                nextPermissions.add('users')
                break
            case 'audits':
                if (nextPermissions.has('audits')) nextPermissions.delete('audits')
                else nextPermissions.add('audits')
                break
            case 'users':
                if (nextPermissions.has('users')) nextPermissions.delete('users')
                else nextPermissions.add('users')
                break
            case 'checkins':
                if (nextPermissions.has('checkins')) nextPermissions.delete('checkins')
                else nextPermissions.add('checkins')
                break
            default:
        }

        onChange(Array.from(nextPermissions))
    }
    return (
        <Container>
            {PERMISSION_CONTENT.map((scope) => (
                <Column key={scope.key}>
                    <Column>
                        <PermissionItemElement
                            isScope
                            checked={adminSelected}
                            label={scope.key}
                            description={scope.description}
                            onClick={() => handleSelect(scope.key)}
                        />
                        {scope.content.map((permission) => (
                            <PermissionItemElement
                                key={permission.key}
                                checked={permissions.has(permission.key)}
                                label={permission.key}
                                description={permission.description}
                                onClick={() => handleSelect(permission.key)}
                            />
                        ))}
                    </Column>
                </Column>
            ))}
        </Container>
    )
}

const Container = styled.div``

const GridRow = styled.div`
    display: grid;
    grid-template-columns: 120px 1fr;
`
const Row = styled.div<{ disabled?: boolean }>`
    flex-flow: row nowrap;
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    ${(p) => (p.disabled ? 'opacity: 0.5;' : '')}
`
const Column = styled.div`
    flex-flow: column nowrap;
    display: flex;
    flex: 1 1 auto;
    justify-content: center;
`

const PermissionLabel = styled.span<{ isScope?: boolean }>`
    ${text('paragraph', 'primary', 'text')}
    ${(p) => (p.isScope ? text('paragraph', 'primary', 'text', 'bold') : '')}
`

const DescriptionLabel = styled.span`
    ${text('paragraph', 'secondary', 'text')}
    white-space: nowrap;
`
